import { useState, useCallback } from 'react';
import { API_BASE_URL } from '../components/config/api';

export interface NormalizedIngredient {
  id: number;
  ingredient_id: number;
  raw_material_id: number;
  name: string;
  is_removable: boolean;
  is_extra_available: boolean;
  is_default: boolean;
  extra_price: number;
  price: number;
  extra_quantity: number;
  quantity_used: number;
}

interface UseProductIngredientsReturn {
  ingredients: NormalizedIngredient[];
  extras: NormalizedIngredient[];
  loading: boolean;
  fetchBySize: (sizeId: number, storeId?: number) => Promise<void>;
  fetchByProduct: (productId: number, storeId: number) => Promise<void>;
  clear: () => void;
}

const normalizeIngredient = (ing: any): NormalizedIngredient => {
  const id = ing.ingredient_id || ing.id;
  const price = ing.extra_price || 0;
  return {
    id,
    ingredient_id: id,
    raw_material_id: ing.raw_material_id || id,
    name: ing.ingredient_name || ing.name,
    is_removable: ing.is_removable !== false,
    is_extra_available: ing.is_extra_available || false,
    is_default: ing.is_default !== false,
    extra_price: price,
    price,
    extra_quantity: ing.extra_quantity || 1,
    quantity_used: ing.quantity_used || ing.quantity_needed || 1
  };
};

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
});

export function useProductIngredients(): UseProductIngredientsReturn {
  const [ingredients, setIngredients] = useState<NormalizedIngredient[]>([]);
  const [extras, setExtras] = useState<NormalizedIngredient[]>([]);
  const [loading, setLoading] = useState(false);

  const clear = useCallback(() => {
    setIngredients([]);
    setExtras([]);
  }, []);

  const fetchBySize = useCallback(async (sizeId: number, storeId?: number) => {
    setLoading(true);
    try {
      // Support both URL patterns used across the app
      const url = storeId
        ? `${API_BASE_URL}/api/products/${storeId}/sizes/${sizeId}/ingredients`
        : `${API_BASE_URL}/api/products/sizes/${sizeId}/ingredients`;

      const response = await fetch(url, { headers: getAuthHeaders() });

      if (response.ok) {
        const data = await response.json();
        const normalized = (data.ingredients || []).map(normalizeIngredient);
        setIngredients(normalized);
        setExtras(normalized.filter((i: NormalizedIngredient) => i.is_extra_available));
      } else {
        clear();
      }
    } catch (error) {
      console.error('Failed to fetch size ingredients:', error);
      clear();
    } finally {
      setLoading(false);
    }
  }, [clear]);

  const fetchByProduct = useCallback(async (productId: number, storeId: number) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/products/${storeId}/products/${productId}/ingredients`,
        { headers: getAuthHeaders() }
      );

      if (response.ok) {
        const data = await response.json();
        const normalized = (data.ingredients || []).map(normalizeIngredient);
        setIngredients(normalized);
        setExtras(normalized.filter((i: NormalizedIngredient) => i.is_extra_available));
      } else {
        clear();
      }
    } catch (error) {
      console.error('Failed to fetch product ingredients:', error);
      clear();
    } finally {
      setLoading(false);
    }
  }, [clear]);

  return { ingredients, extras, loading, fetchBySize, fetchByProduct, clear };
}
